// Pure helpers for the Reports, Search, Claude access and privacy screens.
// Calendar dates are "YYYY-MM-DD" strings handled as plain dates (UTC math),
// never through the browser's time zone (see format.js).
import { addDays, formatDay } from "./format";

const YMD = /^(\d{4})-(\d{2})-(\d{2})$/;
const parse = (ymd) => {
    const m = YMD.exec(ymd || "");
    return m ? { y: +m[1], m: +m[2], d: +m[3] } : null;
};
const pad = (n) => String(n).padStart(2, "0");

// Monday of the (Monday to Sunday) week that contains ymd.
export function weekStartOf(ymd) {
    const p = parse(ymd);
    if (!p) return "";
    const dow = new Date(Date.UTC(p.y, p.m - 1, p.d)).getUTCDay(); // 0 = Sunday
    return addDays(ymd, -((dow + 6) % 7));
}

// "12 Oct – 18 Oct 2026" (both ends of a week or any range).
export function rangeText(from, to, today) {
    if (!from || !to) return "";
    const sameYear = from.slice(0, 4) === to.slice(0, 4);
    const a = sameYear ? formatDay(from, { short: true, today: to }) : formatDay(from);
    return `${a} – ${formatDay(to)}`;
}

// Quarter of a date: {label: "2026Q4", from, to}.
export function quarterOf(ymd) {
    const p = parse(ymd);
    if (!p) return null;
    const q = Math.floor((p.m - 1) / 3) + 1;
    const startMonth = (q - 1) * 3 + 1;
    const endMonth = startMonth + 2;
    const lastDay = new Date(Date.UTC(p.y, endMonth, 0)).getUTCDate();
    return { label: `${p.y}Q${q}`, from: `${p.y}-${pad(startMonth)}-01`, to: `${p.y}-${pad(endMonth)}-${pad(lastDay)}` };
}

// The quarter before the one containing ymd.
export function previousQuarter(ymd) {
    const q = quarterOf(ymd);
    return q ? quarterOf(addDays(q.from, -1)) : null;
}

// "2026Q4" -> "Q4 2026"
export const quarterText = (label) => {
    const m = /^(\d{4})Q([1-4])$/.exec(label || "");
    return m ? `Q${m[2]} ${m[1]}` : label || "";
};

export const DASH = "—";

// €12,000 ; null/undefined -> "—". { compact: true } -> €12k / €1.2M.
export function formatEUR(v, { compact = false } = {}) {
    if (v === null || v === undefined || Number.isNaN(Number(v))) return DASH;
    const n = Number(v);
    if (compact && Math.abs(n) >= 1000) {
        if (Math.abs(n) >= 1e6) return `€${trim(n / 1e6, 1)}M`;
        return `€${trim(n / 1e3, 1)}k`;
    }
    return "€" + n.toLocaleString("en-GB", { maximumFractionDigits: n % 1 === 0 ? 0 : 2 });
}

const trim = (n, digits) => String(Number(n.toFixed(digits)));

// Ratio 0.667 -> "66.7%"; null -> "—".
export function formatPercent(ratio, digits = 1) {
    if (ratio === null || ratio === undefined) return DASH;
    return `${trim(Number(ratio) * 100, digits)}%`;
}

// Plain number with up to `digits` decimals; null -> "—".
export function formatNumber(v, digits = 2) {
    if (v === null || v === undefined) return DASH;
    return Number(v).toLocaleString("en-GB", { maximumFractionDigits: digits });
}

// Funnel ratio keys, how to show each, and the counts they come from.
export const RATIOS = [
    { key: "touch_to_introduction", label: "Touch → introduction", kind: "percent" },
    { key: "meeting_to_proposal", label: "Meeting → proposal", kind: "percent" },
    { key: "proposal_to_win", label: "Proposal → win", kind: "percent" },
    { key: "meetings_per_win", label: "Meetings per win", kind: "number" },
    { key: "average_deal_size_eur", label: "Average deal size", kind: "eur" },
    { key: "value_per_meeting_eur", label: "Value per meeting", kind: "eur" },
];

export function formatRatio(value, kind) {
    if (kind === "percent") return formatPercent(value);
    if (kind === "eur") return formatEUR(value);
    return formatNumber(value, 2);
}

export const FUNNEL_COUNTS = [
    { key: "touches", label: "Touches" },
    { key: "introductions", label: "Introductions" },
    { key: "first_meetings", label: "First meetings" },
    { key: "proposals", label: "Proposals" },
    { key: "wins", label: "Wins" },
    { key: "contracted_value_eur", label: "Contracted value", eur: true },
];

export const LEAD_SOURCES = [
    { value: "introduction", label: "Introduction" },
    { value: "signal", label: "Signal" },
    { value: "direct", label: "Direct" },
    { value: "inbound", label: "Inbound" },
];

export const SIGNAL_TYPES = [
    { value: "layoffs", label: "Layoffs" },
    { value: "leadership_hire", label: "Leadership hire" },
    { value: "funding", label: "Funding" },
    { value: "leadership_change", label: "Leadership change" },
    { value: "regulatory_event", label: "Regulatory event" },
];

// Target progress for a bar: {reached, time} as 0-100 numbers (reached may
// pass 100 in the label but the bar stops at 100).
export function targetProgress(t) {
    if (!t) return { reached: 0, time: 0, hasTarget: false };
    const time = t.days_total ? Math.min(100, (t.days_elapsed / t.days_total) * 100) : 0;
    if (!t.target_eur) return { reached: 0, time, hasTarget: false };
    const reached = Math.max(0, Math.min(100, (t.contracted_eur / t.target_eur) * 100));
    return { reached, time, hasTarget: true };
}

// Bars for a chart: scale values to 0-100 of the largest (0 when all are 0).
export function scaleBars(values) {
    const max = Math.max(0, ...values.map((v) => Number(v) || 0));
    return values.map((v) => (max ? ((Number(v) || 0) / max) * 100 : 0));
}

// ---------- search ----------

export const SEARCH_TYPES = [
    { value: "contact", label: "People", icon: "ri-user-line" },
    { value: "company", label: "Companies", icon: "ri-building-line" },
    { value: "lead", label: "Leads", icon: "ri-funds-line" },
    { value: "signal", label: "Signals", icon: "ri-radar-line" },
];

export const searchIcon = (type) => (SEARCH_TYPES.find((t) => t.value === type) || { icon: "ri-search-line" }).icon;

// Route of a search result in this app.
export function resultPath(r) {
    switch (r.type) {
        case "contact":
            return `/crm/contacts/${r.id}`;
        case "company":
            return `/crm/companies/${r.id}`;
        case "lead":
            return `/crm/leads/${r.id}`;
        case "signal":
            return `/crm/signals?highlight=${r.id}`;
        default:
            return "/search";
    }
}

// Summary without the leading "Contact · " (the type shows as an icon).
export const summaryText = (s) => String(s || "").replace(/^(Contact|Company|Lead|Signal)( · )?/, "");

// Status filter values the search API accepts, grouped for a select.
export const SEARCH_STATUSES = [
    { value: "overdue", label: "Overdue", group: "Due" },
    { value: "due_soon", label: "Due soon", group: "Due" },
    { value: "ok", label: "OK", group: "Due" },
    { value: "snoozed", label: "Snoozed", group: "Due" },
    { value: "open", label: "Open", group: "Leads" },
    { value: "closed", label: "Closed", group: "Leads" },
    { value: "stalled", label: "Stalled", group: "Leads" },
    { value: "no_next_step", label: "No next step", group: "Leads" },
    { value: "acted_on", label: "Acted on", group: "Signals" },
    { value: "not_acted_on", label: "Not acted on", group: "Signals" },
];

// ---------- Claude access ----------

export const MCP_URL = "https://horus.preascent.net/mcp";
export const TOKEN_PLACEHOLDER = "hrs_…";

export const claudeCodeCommand = (token = TOKEN_PLACEHOLDER) =>
    `claude mcp add --transport http --scope user horus ${MCP_URL} \\\n  --header "Authorization: Bearer ${token}"`;

export const claudeDesktopConfig = (token = TOKEN_PLACEHOLDER) =>
    JSON.stringify(
        {
            mcpServers: {
                horus: {
                    command: "npx",
                    // The literal ${HORUS_AUTH} is expanded by mcp-remote from env.
                    // eslint-disable-next-line no-template-curly-in-string
                    args: ["-y", "mcp-remote", MCP_URL, "--header", "Authorization:${HORUS_AUTH}"],
                    env: { HORUS_AUTH: `Bearer ${token}` },
                },
            },
        },
        null,
        2
    );

export const scopeLabel = (scopes) => ((scopes || []).includes("write") ? "Read + write" : "Read");

// Expiry date (Istanbul calendar day) -> RFC 3339 instant at the end of that
// day in Istanbul (UTC+3, no DST): "2027-01-01" -> "2027-01-01T20:59:59Z".
export const expiryInstant = (ymd) => (YMD.test(ymd || "") ? `${ymd}T20:59:59Z` : null);

// ---------- privacy ----------

// Textarea text <-> keyword list: one per line (commas also split), trimmed,
// empty lines dropped, duplicates removed case-insensitively.
export function parseKeywords(text) {
    const seen = new Set();
    const out = [];
    String(text || "")
        .split(/[\n,]/)
        .map((s) => s.trim().replace(/\s+/g, " "))
        .filter(Boolean)
        .forEach((k) => {
            const key = k.toLocaleLowerCase("tr-TR");
            if (seen.has(key)) return;
            seen.add(key);
            out.push(k);
        });
    return out;
}

// Filename from a Content-Disposition header, or null.
export function filenameFromDisposition(header) {
    if (!header) return null;
    const star = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(header);
    if (star) {
        try {
            return decodeURIComponent(star[1].trim().replace(/^"|"$/g, ""));
        } catch (e) {
            // fall through
        }
    }
    const m = /filename="?([^";]+)"?/i.exec(header);
    return m ? m[1].trim() : null;
}

// API write responses may carry {"warnings": [...]} (special-category guard).
export const writeWarnings = (res) => (res && Array.isArray(res.warnings) ? res.warnings.filter(Boolean) : []);
